/* eslint-disable react/prop-types */
import at from 'lodash/at'
import lowerCase from 'lodash/lowerCase'
import React from 'react'
import { Tooltip } from 'react-bootstrap'
import BPopover from 'react-bootstrap/Popover'
import {
  Dropdown,
  FormControl,
  FormGroup,
  FormLabel,
  Popper,
  Radio,
  RadioGroup,
} from '../../../components'
import { COUNTRY, NOT_AVAIABLE_VALUE } from '../../../utils/constants'
import { isNull, isUndefined, numberFormatted } from '../../../utils/fns'
import { formatNumber, formatPercentage } from '../../../utils/utils'
import {
  ANCHORAGE_OPTION,
  FASTENERS_FAMILY_PRODUCT_LINK,
  FOUNDATION_TYPE,
  HOLDOWN_SUBTYPE,
  HOLDOWN_TYPE,
  POST_BASE_MATERIAL_TYPE,
  POST_BASE_SUBTYPE,
  PRODUCT_LINK_OPTIONS,
  PRODUCT_THUMBNAIL_OPTIONS,
  UPLIFT_DURATION
} from './constants'

const SORT = {
  ASC: false,
  DESC: true,
  NONE: 'None',
}

const SELECT_ALL = 'Select All'

export const getLabelQty = (qty, label) =>
  qty === 'N/A' || isUndefined(qty) || isNull(qty)
    ? NOT_AVAIABLE_VALUE
    : `(${numberFormatted(qty)}) ${label}`

const getCanadaInstalledCostLabel = (arr, ici) => {
  const smallest = Math.min(...arr)

  if (numberFormatted(ici) === smallest) {
    return 'Lowest'
  }

  return `+${formatPercentage(((ici - smallest) / smallest) * 100)}`
}

const FastenersFamilyProductLink = ({ value, fastenersFamily }) => {
  return fastenersFamily && Object.hasOwn(FASTENERS_FAMILY_PRODUCT_LINK, fastenersFamily) ? (
    <a
      href={FASTENERS_FAMILY_PRODUCT_LINK[fastenersFamily]}
      role="button"
      target="_blank"
      rel="noreferrer"
    >
      <span className="button__text">{value}</span>
    </a>
  ) : (
    <span className="button__text">{value}</span>
  )
}

export const getInstalledCostLabel = (ici) =>
  ici === 999999 ? '-' : ici === 100 ? 'Lowest' : `+${formatPercentage(ici - 100)}`

const STEEL_GRADE_LABEL = {
  F1554_GR36: 'F1554_GR36 (RFB Retrofit Bolt)',
}

const STEEL_GRADE_URL = {
  [STEEL_GRADE_LABEL.F1554_GR36]:
    'https://www.strongtie.com/miscadhesiveaccessories_adhesiveanchoringaccessories/rfb_bolt/p/rfb',
}

const formatAnchorSteelGradeValue = (value) => STEEL_GRADE_LABEL[value] || value

const AnchorSteelGradeCell = ({ value }) => {
  const url = STEEL_GRADE_URL[value]
  return url ? (
    <a href={url} target="_blank" rel="noreferrer">
      {value}
    </a>
  ) : (
    value
  )
}

const sortByInstalledCost = (rowA, rowB, columnId) => {
  const valueA = at(rowA.original, [columnId])
  const valueB = at(rowB.original, [columnId])
  return valueA - valueB
}

export const hdTypeA = [
  {
    accessor: 'holdownPostInstalled.holdownModel',
    Header: 'Holdown Model',
    Cell: ({ value, row }) => (
      <PopperProductThumbnail
        imgSrc={PRODUCT_THUMBNAIL_OPTIONS[row.original.holdownPostInstalled?.productFamily]}
      >
        <a
          href={
            PRODUCT_LINK_OPTIONS[row.original.holdownPostInstalled?.productFamily] ||
            PRODUCT_LINK_OPTIONS.default
          }
          role="button"
          target="_blank"
          rel="noreferrer"
        >
          <img
            src={PRODUCT_THUMBNAIL_OPTIONS[row.original.holdownPostInstalled?.productFamily]}
            className="img-product-thumbnail"
            alt={value}
          />
          <span className="button__text">{value}</span>
        </a>
      </PopperProductThumbnail>
    ),
  },
  {
    Header: 'Installed Cost',
    id: 'holdownPostInstalled.installedCostIndex',
    accessor: (row) => getInstalledCostLabel(row.holdownPostInstalled?.installedCostIndex),
    sortType: sortByInstalledCost,
  },
  {
    accessor: 'holdownPostInstalled.postSize',
    Header: 'Post Size (in.)',
  },
  {
    id: 'holdownPostInstalled.holdownCapacity',
    Header: 'Holdown Allowable Load (lbs)',
    accessor: (row) => formatNumber(row.holdownPostInstalled?.holdownCapacity),
  },
  {
    accessor: 'holdownPostInstalled.deflectionASD',
    Header: 'Deflection at Demand Load (in)',
  },
  {
    accessor: 'holdownPostInstalled.holdownFastenersModel',
    Header: 'Post Fasteners',
    Cell: ({ value, row }) => (
      <FastenersFamilyProductLink
        fastenersFamily={row.original.holdownPostInstalled?.postFastenersFamily}
        value={value}
      />
    ),
  },
  {
    accessor: 'holdownPostInstalled.anchorDiameter',
    Header: 'Anchor Diameter (in)',
  },
]

export const hdTypeB = [
  {
    accessor: 'holdownCastInPlace.holdownModel',
    Header: 'Holdown Model',
    Cell: ({ value, row }) => (
      <PopperProductThumbnail
        imgSrc={PRODUCT_THUMBNAIL_OPTIONS[row.original.holdownCastInPlace?.productFamily]}
      >
        <a
          href={
            PRODUCT_LINK_OPTIONS[row.original.holdownCastInPlace?.productFamily] ||
            PRODUCT_LINK_OPTIONS.default
          }
          role="button"
          target="_blank"
          rel="noreferrer"
        >
          <img
            src={PRODUCT_THUMBNAIL_OPTIONS[row.original.holdownCastInPlace?.productFamily]}
            className="img-product-thumbnail"
            alt={value}
          />
          <span className="button__text">{value}</span>
        </a>
      </PopperProductThumbnail>
    ),
  },
  {
    id: 'holdownCastInPlace.installedCostIndex',
    Header: 'Installed Cost',
    accessor: (row) => getInstalledCostLabel(row.holdownCastInPlace?.installedCostIndex),
    sortType: sortByInstalledCost,
  },
  {
    accessor: 'holdownCastInPlace.postSize',
    Header: 'Post Size (in.)',
  },
  {
    id: 'foundationAnchorDesigner.upliftLoadASD',
    Header: 'Holdown Allowable Load (lbs)',
    accessor: (row) => formatNumber(row.foundationAnchorDesigner?.upliftLoadASD),
  },
  {
    accessor: 'holdownCastInPlace.deflectionASD',
    Header: 'Deflection at Demand Load (in)',
  },
  {
    accessor: 'holdownCastInPlace.holdownFastenersModel',
    Header: 'Post Fasteners',
    Cell: ({ value, row }) => (
      <FastenersFamilyProductLink
        fastenersFamily={row.original.holdownCastInPlace?.postFastenersFamily}
        value={value}
      />
    ),
  },
  {
    accessor: 'holdownCastInPlace.anchorPlacement.a',
    Header: 'Min Embedment Depth (in)',
  },
  {
    accessor: 'holdownCastInPlace.anchorPlacement.c',
    Header: 'Min Edge Distance (in)',
  },
  {
    accessor: 'holdownCastInPlace.anchorPlacement.b',
    Header: 'Min End Distance (in)',
  },
  {
    accessor: 'foundationAnchorDesigner.footingDimmensions.a',
    Header: 'Min Concrete Thickness (in)',
  },
  {
    accessor: 'foundationAnchorDesigner.footingDimmensions.f',
    Header: 'Min Stemwall Thickness (in)',
    Cell: ({ value }) => (value > 0 ? value : NOT_AVAIABLE_VALUE),
  },
]

export const postBaseTableHeaders = {
  [POST_BASE_SUBTYPE.POST_INSTALLED]: {
    [POST_BASE_MATERIAL_TYPE.CONCRETE]: [
      {
        accessor: 'postBaseModel',
        Header: 'Model',
        Cell: ({ value, row }) => (
          <PopperProductThumbnail imgSrc={PRODUCT_THUMBNAIL_OPTIONS[row.original.productFamily]}>
            <a
              href={
                PRODUCT_LINK_OPTIONS[row.original.productFamily] || PRODUCT_LINK_OPTIONS.default
              }
              target="_blank"
              rel="noreferrer"
            >
              <img
                src={PRODUCT_THUMBNAIL_OPTIONS[row.original.productFamily]}
                className="img-product-thumbnail"
                alt={value}
              />
              {value}
            </a>
          </PopperProductThumbnail>
        ),
      },
      {
        id: 'installedCostIndex',
        Header: 'Installed Cost',
        accessor: (row) => getInstalledCostLabel(row.installedCostIndex),
        sortType: sortByInstalledCost,
      },
      { accessor: 'postSize', Header: 'Post Size (in.)' },
      { accessor: 'productCoating', Header: 'Coating/Material' },
      {
        id: 'postBaseFastenersModel',
        Header: 'Post Fasteners',
        accessor: (row) =>
          `(${Number(row.postBaseFastenersQuantity).toFixed(0)}) ${row.postBaseFastenersModel}`,
        Cell: ({ value, row }) => (
          <FastenersFamilyProductLink
            value={value}
            fastenersFamily={row.original?.postFastenersFamily}
          />
        ),
      },
      {
        id: 'steelAnchorDiameter',
        Header: 'Anchor (qty) dia.',
        accessor: (row) =>
          isUndefined(row.steelAnchorDiameter)
            ? NOT_AVAIABLE_VALUE
            : `(${Number(row.resistingQuantity).toFixed(0)}) ${row.steelAnchorDiameter}`,
      },
      {
        id: 'upliftLoadASD',
        Header: 'Uplift (lbs)',
        accessor: (row) => formatNumber(row.upliftLoadASD),
      },
      {
        id: 'downLoadASD',
        Header: 'Download (lbs)',
        accessor: (row) => formatNumber(row.downLoadASD),
      },
      {
        id: 'lateralF1',
        Header: 'Lateral F1 (lbs)',
        accessor: (row) => formatNumber(row.lateralF1),
      },
      {
        id: 'lateralF2',
        Header: 'Lateral F2 (lbs)',
        accessor: (row) => formatNumber(row.lateralF2),
      },
    ],
    [POST_BASE_MATERIAL_TYPE.WOOD]: [
      {
        accessor: 'postBaseModel',
        Header: 'Model',
        Cell: ({ value, row }) => (
          <PopperProductThumbnail imgSrc={PRODUCT_THUMBNAIL_OPTIONS[row.original.productFamily]}>
            <a
              href={
                PRODUCT_LINK_OPTIONS[row.original.productFamily] || PRODUCT_LINK_OPTIONS.default
              }
              target="_blank"
              rel="noreferrer"
            >
              <img
                src={PRODUCT_THUMBNAIL_OPTIONS[row.original.productFamily]}
                className="img-product-thumbnail"
                alt={value}
              />
              {value}
            </a>
          </PopperProductThumbnail>
        ),
      },
      {
        id: 'installedCostIndex',
        Header: 'Installed Cost',
        accessor: (row) => getInstalledCostLabel(row.installedCostIndex),
        sortType: sortByInstalledCost,
      },
      { accessor: 'postSize', Header: 'Post Size (in.)' },
      { accessor: 'productCoating', Header: 'Coating/Material' },
      {
        id: 'postBaseFastenersModel',
        Header: 'Post Fasteners',
        accessor: (row) =>
          `(${Number(row.postBaseFastenersQuantity).toFixed(0)}) ${row.postBaseFastenersModel}`,
        Cell: ({ value, row }) => (
          <FastenersFamilyProductLink
            value={value}
            fastenersFamily={row.original?.postFastenersFamily}
          />
        ),
      },
      {
        accessor: 'postBaseFastenersModelWood',
        Header: 'Foundation Fasteners',
        Cell: ({ value, row }) => (
          <FastenersFamilyProductLink
            value={`(${Number(row.original.postBaseFastenersQuantityWood).toFixed(0)}) ${value}`}
            fastenersFamily={row.original?.foundationFastenersFamily}
          />
        ),
      },
      {
        id: 'upliftLoadASD',
        Header: 'Uplift (lbs)',
        accessor: (row) => formatNumber(row.upliftLoadASD),
      },
      {
        id: 'lateralF1',
        Header: 'Lateral F1 (lbs)',
        accessor: (row) => formatNumber(row.lateralF1),
      },
    ],
  },
  [POST_BASE_SUBTYPE.CAST_IN_PLACE]: [
    {
      accessor: 'postBaseModel',
      Header: 'Model',
      Cell: ({ value, row }) => (
        <PopperProductThumbnail imgSrc={PRODUCT_THUMBNAIL_OPTIONS[row.original.productFamily]}>
          <a
            href={PRODUCT_LINK_OPTIONS[row.original.productFamily] || PRODUCT_LINK_OPTIONS.default}
            target="_blank"
            rel="noreferrer"
          >
            <img
              src={PRODUCT_THUMBNAIL_OPTIONS[row.original.productFamily]}
              className="img-product-thumbnail"
              alt={value}
            />
            {value}
          </a>
        </PopperProductThumbnail>
      ),
    },
    {
      id: 'installedCostIndex',
      Header: 'Installed Cost',
      accessor: (row) => getInstalledCostLabel(row.installedCostIndex),
      sortType: sortByInstalledCost,
    },
    { accessor: 'postSize', Header: 'Post Size (in.)' },
    { accessor: 'productCoating', Header: 'Coating/Material' },
    {
      id: 'postBaseFastenersModel',
      Header: 'Post Fasteners',
      accessor: (row) =>
        `(${Number(row.postBaseFastenersQuantity).toFixed(0)}) ${row.postBaseFastenersModel}`,
      Cell: ({ value, row }) => (
        <FastenersFamilyProductLink
          value={value}
          fastenersFamily={row.original?.postFastenersFamily}
        />
      ),
    },
    {
      id: 'diameterMechanicalAnchor',
      Header: 'Anchor (qty) dia.',
      accessor: (row) =>
        isUndefined(row.diameterMechanicalAnchor)
          ? NOT_AVAIABLE_VALUE
          : `(${Number(row.quantityMechanicalAnchor).toFixed(0)}) ${row.diameterMechanicalAnchor}`,
    },
    {
      id: 'upliftLoadASD',
      Header: 'Uplift (lbs)',
      accessor: (row) => formatNumber(row.upliftLoadASD),
    },
    {
      id: 'downLoadASD',
      Header: 'Download (lbs)',
      accessor: (row) => formatNumber(row.downLoadASD),
    },
    {
      id: 'lateralF1',
      Header: 'Lateral F1 (lbs)',
      accessor: (row) => formatNumber(row.lateralF1),
    },
    {
      id: 'lateralF2',
      Header: 'Lateral F2 (lbs)',
      accessor: (row) => formatNumber(row.lateralF2),
    },
  ],
}

const getEmbeddedAnchorHeader = (anchorageOption, isHoldownDesign) => {
  const steelGradeColumn = Object.assign(
    {
      accessor: 'foundationAnchorDesigner.steelGrade',
      Header: 'Anchor Steel Grade',
    },
    anchorageOption === ANCHORAGE_OPTION.ADHESIVE && {
      id: 'foundationAnchorDesigner.steelGrade',
      accessor: (row) => formatAnchorSteelGradeValue(row.foundationAnchorDesigner?.steelGrade),
      Cell: AnchorSteelGradeCell,
    },
  )

  const diameterColumn = {
    accessor: 'foundationAnchorDesigner.anchorDiameter',
    Header: 'Anchor Diameter (in)',
  }

  const allowableLoadColumn = {
    accessor: 'foundationAnchorDesigner.upliftLoadASD',
    Header: isHoldownDesign ? 'Anchor Allowable Load (lbs)' : 'Allowable Tension Load (lbs)',
  }

  return [
    {
      accessor: 'foundationAnchorDesigner.anchorProduct',
      Header: 'Anchor Product',
      Cell: ({ value, row }) => (
        <PopperProductThumbnail
          imgSrc={PRODUCT_THUMBNAIL_OPTIONS[row.original.foundationAnchorDesigner?.productFamily]}
        >
          <a
            href={
              PRODUCT_LINK_OPTIONS[row.original.foundationAnchorDesigner?.productFamily] ||
              PRODUCT_LINK_OPTIONS.default
            }
            role="button"
            target="_blank"
            rel="noreferrer"
          >
            <img
              src={PRODUCT_THUMBNAIL_OPTIONS[row.original.foundationAnchorDesigner?.productFamily]}
              className="img-product-thumbnail"
              alt={value}
            />
            <span className="button__text">{value}</span>
          </a>
        </PopperProductThumbnail>
      ),
    },
    ...(isHoldownDesign ? [steelGradeColumn, diameterColumn] : [diameterColumn, steelGradeColumn]),
    allowableLoadColumn,
    {
      accessor: 'foundationAnchorDesigner.anchorPlacement.a',
      Header: 'Min Embedment Depth (in)',
    },
    {
      accessor: 'foundationAnchorDesigner.anchorPlacement.c',
      Header: 'Min Edge Distance (in)',
    },
    {
      accessor: 'foundationAnchorDesigner.anchorPlacement.b',
      Header: 'Min End Distance (in)',
    },
    {
      accessor: 'foundationAnchorDesigner.footingDimmensions.a',
      Header: 'Min Concrete Thickness (in)',
    },
    {
      accessor: 'foundationAnchorDesigner.footingDimmensions.f',
      Header: 'Min Stemwall Thickness (in)',
      Cell: ({ value }) => (value > 0 ? value : NOT_AVAIABLE_VALUE),
    },
  ]
}

const getPostBaseDesignHeader = (postBaseSubType, postBaseMaterialType) => ({
  summaryHeader:
    postBaseTableHeaders[postBaseSubType][postBaseMaterialType] ||
    postBaseTableHeaders[postBaseSubType],
  defaultSorted: [
    {
      id: 'installedCostIndex',
      desc: false,
    },
  ],
})

export const getHoldownAnchorageDesignHeader = (holdownSubType, anchorageOption) => {
  if (holdownSubType === HOLDOWN_SUBTYPE.EMBEDDED_STRAP) {
    // 1B
    return {
      summaryHeader: hdTypeB,
      defaultSorted: [
        {
          id: 'holdownCastInPlace.installedCostIndex',
          desc: false,
        },
      ],
    }
  } else
    return {
      summaryHeader: [
        {
          accessor: 'holdownPostInstalled.holdownModel',
          Header: 'Holdown Model',
          Cell: ({ value, row }) => (
            <PopperProductThumbnail
              imgSrc={PRODUCT_THUMBNAIL_OPTIONS[row.original.holdownPostInstalled?.productFamily]}
            >
              <a
                href={
                  PRODUCT_LINK_OPTIONS[row.original.holdownPostInstalled?.productFamily] ||
                  PRODUCT_LINK_OPTIONS.default
                }
                role="button"
                target="_blank"
                rel="noreferrer"
              >
                <img
                  src={PRODUCT_THUMBNAIL_OPTIONS[row.original.holdownPostInstalled?.productFamily]}
                  className="img-product-thumbnail"
                  alt={value}
                />
                <span className="button__text">{value}</span>
              </a>
            </PopperProductThumbnail>
          ),
        },
        {
          id: 'holdownPostInstalled.installedCostIndex',
          Header: 'Installed Cost',
          accessor: (row) => getInstalledCostLabel(row.holdownPostInstalled?.installedCostIndex),
          sortType: sortByInstalledCost,
        },
        {
          accessor: 'holdownPostInstalled.postSize',
          Header: 'Post Size (in.)',
        },
        {
          id: 'holdownPostInstalled.holdownCapacity',
          Header: 'Holdown Allowable Load (lbs)',
          accessor: (row) => formatNumber(row.holdownPostInstalled?.holdownCapacity),
          Cell: ({ value }) => value || '',
        },
        {
          accessor: 'holdownPostInstalled.deflectionASD',
          Header: 'Deflection at Demand Load (in)',
        },
        {
          accessor: 'holdownPostInstalled.holdownFastenersModel',
          Header: 'Post Fasteners',
          Cell: ({ value, row }) => (
            <FastenersFamilyProductLink
              fastenersFamily={row.original.holdownPostInstalled?.postFastenersFamily}
              value={value}
            />
          ),
        },
        ...getEmbeddedAnchorHeader(anchorageOption, true),
      ],
      defaultSorted: [
        {
          id: 'holdownPostInstalled.installedCostIndex',
          desc: false,
        },
      ],
    }
}

const getHoldownDesignHeader = () => {
  return {
    summaryHeader: hdTypeA,
    defaultSorted: [
      {
        id: 'holdownPostInstalled.installedCostIndex',
        desc: false,
      },
    ],
  }
}

const getAnchorageDesignHeader = (anchorageOption) => ({
  summaryHeader: getEmbeddedAnchorHeader(anchorageOption),
  defaultSorted: [
    {
      id: 'foundationAnchorDesigner.anchorDiameter',
      desc: false,
    },
  ],
})

export const holdownDesignByHoldownTypeTableHeaders = ({
  upliftDuration,
  factoredUpliftLRFD,
  summary,
}) => {
  const arrayInstalledCostIndex = summary
    .map((item) => item.holdownPostInstalled?.installedCostIndex || item.ici)
    .sort((a, b) => a - b)

  return {
    [HOLDOWN_TYPE.POST_INSTALLED]: [
      {
        accessor: 'holdownModel',
        Header: 'Model',
        Cell: ({ value, row }) => (
          <PopperProductThumbnail imgSrc={PRODUCT_THUMBNAIL_OPTIONS[row.original.productFamily]}>
            <a
              href={
                PRODUCT_LINK_OPTIONS[row.original.productFamily] || PRODUCT_LINK_OPTIONS.default
              }
              role="button"
              target="_blank"
              rel="noreferrer"
            >
              <img
                src={PRODUCT_THUMBNAIL_OPTIONS[row.original.productFamily]}
                className="img-product-thumbnail"
                alt={value}
              />
              <span className="button__text">{value}</span>
            </a>
          </PopperProductThumbnail>
        ),
      },
      {
        id: 'ici',
        Header: 'Installed Cost',
        accessor: (row) => getCanadaInstalledCostLabel(arrayInstalledCostIndex, row.ici),
        sortType: sortByInstalledCost,
      },
      { accessor: 'corrosion', Header: 'Coating/Material' },
      {
        Header: 'Post Fasteners',
        accessor: (row) => getLabelQty(row.holdownFastenersQty, row.holdownFastenersLabel),
        Cell: ({ value, row }) => (
          <FastenersFamilyProductLink
            value={value}
            fastenersFamily={row.original.postFastenersFamily}
          />
        ),
      },
      {
        accessor: (row) => getLabelQty(row.anchorQty, row.anchorDiameter),
        Header: 'Anchor (qty) dia.',
      },
      {
        accessor: (row) =>
          upliftDuration === UPLIFT_DURATION.SHORT
            ? row.factoredUplift_115
            : row.factoredUplift_100,
        Header: 'Factored Uplift Resistance (lbs)',
      },
      {
        accessor: (row) => {
          const factoredUplift = numberFormatted(
            upliftDuration === UPLIFT_DURATION.SHORT
              ? row.factoredUplift_115
              : row.factoredUplift_100,
          )

          const baseFactoredUpliftLRFD = numberFormatted(factoredUpliftLRFD)

          const result = (baseFactoredUpliftLRFD / factoredUplift) * row.deflectionLRFDMax

          return result.toFixed(3)
        },
        Header: 'Deflection at Factored Load (in)',
      },
    ],

    // not post_installed ( fallback headers )
    ['default']: [
      {
        accessor: 'holdownModel',
        Header: 'Model',
        Cell: ({ value, row }) => (
          <PopperProductThumbnail imgSrc={PRODUCT_THUMBNAIL_OPTIONS[row.original.productFamily]}>
            <a
              href={
                PRODUCT_LINK_OPTIONS[row.original.productFamily] || PRODUCT_LINK_OPTIONS.default
              }
              role="button"
              target="_blank"
              rel="noreferrer"
            >
              <img
                src={PRODUCT_THUMBNAIL_OPTIONS[row.original.productFamily]}
                className="img-product-thumbnail"
                alt={value}
              />
              <span className="button__text">{value}</span>
            </a>
          </PopperProductThumbnail>
        ),
      },
      {
        id: 'ici',
        Header: 'Installed Cost',
        accessor: (row) => getCanadaInstalledCostLabel(arrayInstalledCostIndex, row.ici),
        sortType: sortByInstalledCost,
      },
      { accessor: 'corrosion', Header: 'Coating/Material' },
      {
        accessor: (row) => getLabelQty(row.holdownFastenersQty, row.holdownFastenersLabel),
        Cell: ({ value, row }) => (
          <FastenersFamilyProductLink
            value={value}
            fastenersFamily={row.original.postFastenersFamily}
          />
        ),
        Header: 'Post Fasteners',
      },
      {
        accessor: (row) =>
          upliftDuration === UPLIFT_DURATION.SHORT
            ? row.factoredUplift_115
            : row.factoredUplift_100,
        Header: 'Factored Uplift Resistance (lbs)',
      },
      {
        accessor: (row) => {
          const factoredUplift = numberFormatted(
            upliftDuration === UPLIFT_DURATION.SHORT
              ? row.factoredUplift_115
              : row.factoredUplift_100,
          )

          const baseFactoredUpliftLRFD = numberFormatted(factoredUpliftLRFD)

          const result = (baseFactoredUpliftLRFD / factoredUplift) * row.deflectionLRFDMax

          return result.toFixed(3)
        },
        Header: 'Deflection at Factored Load (in)',
      },
      {
        accessor: 'minStemwallThickness',
        Header: 'Min Stemwall Thickness (in)',
      },
    ],
  }
}

const getHoldownDesignByHoldownTypeHeader = ({
  holdownType,
  upliftDuration,
  factoredUpliftLRFD,
  summary,
}) => {
  const baseSummaryHeader = holdownDesignByHoldownTypeTableHeaders({
    upliftDuration,
    factoredUpliftLRFD,
    summary,
  })
  return {
    summaryHeader: baseSummaryHeader[holdownType] || baseSummaryHeader['default'],
    defaultSorted: [
      {
        id: 'ici',
        desc: false,
      },
    ],
  }
}

export const postBaseDesignByFoundationTypeHeader = ({ upliftDuration, summary }) => {
  const arrayInstalledCostIndex = summary
    .map((item) => item.holdownPostInstalled?.installedCostIndex || item.ici)
    .sort((a, b) => a - b)

  return {
    // not wood ( fallback headers )
    ['default']: [
      {
        accessor: 'postBaseModel',
        Header: 'Model',
        Cell: ({ value, row }) => (
          <PopperProductThumbnail imgSrc={PRODUCT_THUMBNAIL_OPTIONS[row.original.productFamily]}>
            <a
              href={
                PRODUCT_LINK_OPTIONS[row.original.productFamily] || PRODUCT_LINK_OPTIONS.default
              }
              target="_blank"
              rel="noreferrer"
              role="button"
            >
              <img
                src={PRODUCT_THUMBNAIL_OPTIONS[row.original.productFamily]}
                className="img-product-thumbnail"
                alt={value}
              />
              {value}
            </a>
          </PopperProductThumbnail>
        ),
      },
      {
        id: 'ici',
        Header: 'Installed Cost',
        accessor: (row) => getCanadaInstalledCostLabel(arrayInstalledCostIndex, row.ici),
        sortType: sortByInstalledCost,
      },
      { accessor: 'corrosion', Header: 'Coating/Material' },
      {
        accessor: (row) => getLabelQty(row.postBaseFastenersQty, row.postBaseFastenerLabel),
        Cell: ({ value, row }) => (
          <FastenersFamilyProductLink
            value={value}
            fastenersFamily={row.original?.postFastenersFamily}
          />
        ),
        Header: 'Post Fasteners',
      },
      {
        accessor: (row) => getLabelQty(row.resistingFastenerQty, row.resistingFastenerLabel),
        Header: 'Anchor (qty) dia.',
      },
      {
        accessor: (row) =>
          upliftDuration === UPLIFT_DURATION.SHORT
            ? row.factoredUplift_115
            : row.factoredUplift_100,
        Header: 'Factored Uplift Resistance (lbs)',
      },
      {
        accessor: 'factoredDownLoad_100',
        Header: 'Factored Download Resistance (lbs)',
      },
      {
        accessor: (row) => (upliftDuration === UPLIFT_DURATION.SHORT ? row.F1_115 : row.F1_100),
        Header: 'Factored F1 Resistance (lbs)',
      },
      {
        accessor: (row) => (upliftDuration === UPLIFT_DURATION.SHORT ? row.F2_115 : row.F2_100),
        Header: 'Factored F2 Resistance (lbs)',
      },
    ],
    // wood is Minimum 3 ½" Wood
    [FOUNDATION_TYPE.WOOD]: [
      {
        accessor: 'postBaseModel',
        Header: 'Model',
        Cell: ({ value, row }) => (
          <PopperProductThumbnail imgSrc={PRODUCT_THUMBNAIL_OPTIONS[row.original.productFamily]}>
            <a
              href={
                PRODUCT_LINK_OPTIONS[row.original.productFamily] || PRODUCT_LINK_OPTIONS.default
              }
              target="_blank"
              rel="noreferrer"
              role="button"
            >
              <img
                src={PRODUCT_THUMBNAIL_OPTIONS[row.original.productFamily]}
                className="img-product-thumbnail"
                alt={value}
              />
              {value}
            </a>
          </PopperProductThumbnail>
        ),
      },
      {
        id: 'ici',
        Header: 'Installed Cost',
        accessor: (row) => getCanadaInstalledCostLabel(arrayInstalledCostIndex, row.ici),
        sortType: sortByInstalledCost,
      },
      { accessor: 'corrosion', Header: 'Coating/Material' },
      {
        accessor: (row) => getLabelQty(row.postBaseFastenersQty, row.postBaseFastenerLabel),
        Cell: ({ value, row }) => (
          <FastenersFamilyProductLink
            value={value}
            fastenersFamily={row.original?.postFastenersFamily}
          />
        ),
        Header: 'Post Fasteners',
      },
      {
        accessor: (row) => getLabelQty(row.resistingFastenerQty, row.resistingFastenerLabel),
        Cell: ({ value, row }) => (
          <FastenersFamilyProductLink
            value={value}
            fastenersFamily={row.original.foundationFastenersFamily}
          />
        ),
        Header: 'Foundation Fasteners',
      },
      {
        accessor: (row) =>
          upliftDuration === UPLIFT_DURATION.SHORT
            ? row.factoredUplift_115
            : row.factoredUplift_100,
        Header: 'Factored Uplift Resistance (lbs)',
      },
      {
        accessor: (row) => (upliftDuration === UPLIFT_DURATION.SHORT ? row.F1_115 : row.F1_100),
        Header: 'Factored F1 Resistance (lbs)',
      },
    ],
  }
}

const getPostBaseDesignByFoundationTypeHeader = ({ foundationType, upliftDuration, summary }) => {
  const baseSummaryHeader = postBaseDesignByFoundationTypeHeader({
    upliftDuration,
    summary,
  })

  return {
    summaryHeader: baseSummaryHeader[foundationType] || baseSummaryHeader['default'],
    defaultSorted: [
      {
        id: 'ici',
        desc: false,
      },
    ],
  }
}

export const getTableHeader = (input, summary) => {
  const {
    postBaseDesign,
    postBaseSubType,
    postBaseMaterialType,
    holdownDesign,
    foundationDesign: anchorageDesign,
    holdownSubType,
    anchorageOption,
    settings: { country },
    holdownType,
    foundationType,
    upliftDuration,
    factoredUpliftLRFD,
  } = input

  // FOR Canada
  if (country === COUNTRY.CANADA) {
    if (holdownDesign) {
      return getHoldownDesignByHoldownTypeHeader({
        holdownType,
        upliftDuration,
        factoredUpliftLRFD,
        summary,
      })
    }

    if (postBaseDesign) {
      return getPostBaseDesignByFoundationTypeHeader({ foundationType, upliftDuration, summary })
    }

    return { summaryHeader: [], defaultSorted: [] }
  }

  // For USA
  if (postBaseDesign) {
    return getPostBaseDesignHeader(postBaseSubType, postBaseMaterialType)
  } else if (holdownDesign && anchorageDesign) {
    return getHoldownAnchorageDesignHeader(holdownSubType, anchorageOption)
  } else if (
    !anchorageDesign &&
    holdownDesign &&
    holdownSubType === HOLDOWN_SUBTYPE.EMBEDDED_ANCHOR
  ) {
    // 1A
    return getHoldownDesignHeader()
  } else if (anchorageDesign && !holdownDesign) {
    // 1C or 1D or 1E
    return getAnchorageDesignHeader(anchorageOption)
  } else {
    console.log('--error type--', input)
    return { summaryHeader: [], defaultSorted: [] }
  }
}

const PopperProductThumbnail = ({ imgSrc, children }) => (
  <Popper
    trigger={['hover', 'focus']}
    delay={0}
    placement="top"
    overlay={
      <Tooltip>
        <img src={imgSrc} className="popper-product-thumbnail" />
      </Tooltip>
    }
  >
    <div className="popper-child-wrapper">{children}</div>
  </Popper>
)

export function ColumnFilter({ column }) {
  const { filterValue, setFilter, Header } = column

  const handleClose = () => {
    document.body.click()
  }

  return (
    <Popper
      placement="left"
      className
      overlay={
        <BPopover
          onClick={(e) => {
            e.stopPropagation()
          }}
          className="popover-column-filter"
        >
          <div className="workspace">
            <div className="popoverClose" onClick={handleClose} />
            <FormGroup>
              <FormLabel htmlFor={'dropdown'}>Sort</FormLabel>
              <RadioGroup
                name="sort"
                selectedValue={column.isSorted ? column.isSortedDesc : SORT.NONE}
                onChange={(e) => {
                  if (e.target.value === SORT.NONE) {
                    return column.clearSortBy()
                  }

                  return column.toggleSortBy(e.target.value)
                }}
              >
                {[
                  {
                    text: 'Sort A to Z (Smallest to Largest)',
                    value: SORT.ASC,
                  },
                  {
                    text: 'Sort Z to A (Largest to Smallest)',
                    value: SORT.DESC,
                  },
                  {
                    text: SORT.NONE,
                    value: SORT.NONE,
                  },
                ].map((item) => (
                  <label key={item.value} className="inputLabel">
                    <Radio value={item.value} />
                    <span>{item.text}</span>
                  </label>
                ))}
              </RadioGroup>
            </FormGroup>

            <FormGroup>
              <FormLabel htmlFor="byText">Seach by text</FormLabel>
              <FormControl
                name="byText"
                value={filterValue?.text || ''}
                onChange={(e) => {
                  setFilter(e.target.value ? { text: e.target.value } : undefined)
                }}
                placeholder={`Search ${lowerCase(Header)}...`}
              />
            </FormGroup>

            <SelectFilter column={column} />
          </div>
        </BPopover>
      }
    >
      {filterValue ? (
        <span className="icon icon-filter" />
      ) : (
        <span className="icon icon-more-horiz" />
      )}
    </Popper>
  )
}

export function SelectFilter({ column }) {
  const { filterValue, setFilter, preFilteredRows, id } = column

  const options = React.useMemo(() => {
    const opts = new Set()
    preFilteredRows.forEach((row) => {
      opts.add(row.values[id])
    })
    return [...opts]
  }, [id, preFilteredRows])

  const dropdownOptions = [
    { text: SELECT_ALL, value: SELECT_ALL },
    ...options.map((option) => ({ text: option, value: option })),
  ]

  return (
    <FormGroup>
      <FormLabel htmlFor={'dropdown'}>Search by option</FormLabel>
      <Dropdown
        name="dropdown"
        search
        clearable
        floating
        upward
        value={filterValue?.select || SELECT_ALL}
        options={dropdownOptions}
        onChange={(e) => {
          const value = e.target.value
          setFilter(value === SELECT_ALL ? undefined : { select: value })
        }}
      />
    </FormGroup>
  )
}
